import { useId, useState } from 'react';
import {
  NOTICE_BODY_MAX,
  NOTICE_TITLE_MAX,
  NOTICE_TITLE_MIN,
  berlinDay,
  de,
  mayManageNotices,
  noticeLevelLabel,
  noticeLevelsFor,
  type MemberRole,
  type NoticeLevel,
} from '@fsv/core';
import { formatDate, formatDateTime, formatNoticeDay, noticesOfAircraft, tx, type AircraftNotice } from '@fsv/data';
import { useNoticeHistory, useNotices, useSettings, useWrite } from '../data/hooks';
import { ChoiceChips } from './Controls';
import { Icon } from './Icon';
import { Sheet } from './Sheet';
import './NoticePanel.css';

/**
 * The notices of the technicians on top of an aircraft's page (0022): what a
 * pilot should know before the flight. Everybody reads and confirms „gelesen“;
 * Technik and admins also add, change and resolve, see who read, and look
 * back at resolved ones. Nothing is shown to a member when there is nothing.
 */
export function NoticePanel({ aircraftId, registration, role }: { aircraftId: string; registration: string; role: MemberRole }) {
  const notices = useNotices();
  const editor = mayManageNotices(role);
  const [editing, setEditing] = useState<AircraftNotice | 'new' | null>(null);
  const [history, setHistory] = useState(false);
  const own = noticesOfAircraft(notices.data, aircraftId);
  if (!editor && own.length === 0) return null;
  return (
    <section className="notices" aria-label={de.notices.title} data-testid="notice-panel">
      <div className="notices__head">
        <h2 className="notices__title">{de.notices.title}</h2>
        {editor && (
          <div className="notices__tools">
            <button type="button" className="btn btn--sm" onClick={() => setHistory((h) => !h)} aria-expanded={history}>
              <Icon name="history" />
              {de.notices.history}
            </button>
            <button type="button" className="btn btn--sm btn--primary" onClick={() => setEditing('new')}>
              <Icon name="plus" />
              {de.notices.add}
            </button>
          </div>
        )}
      </div>
      {own.map((n) => (
        <NoticeCard key={n.id} notice={n} editor={editor} onEdit={() => setEditing(n)} />
      ))}
      {history && <NoticeHistory aircraftId={aircraftId} />}
      {editing && (
        <NoticeSheet
          aircraftId={aircraftId}
          registration={registration}
          notice={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}

function NoticeCard({ notice, editor, onEdit }: { notice: AircraftNotice; editor: boolean; onEdit: () => void }) {
  const write = useWrite();
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const grounded = notice.level === 'gesperrt';

  const acknowledge = async () => {
    setBusy(true);
    await write({ run: async (s) => ({ value: await s.acknowledgeNotice(notice.id), eventId: 0 }), success: de.notices.acknowledgedToast });
    setBusy(false);
  };
  const resolve = async () => {
    setBusy(true);
    await write({ run: (s) => s.resolveNotice(notice.id), success: de.notices.resolved });
    setBusy(false);
    setAsking(false);
  };

  return (
    <article className={`ncard ncard--${notice.level}`} data-testid="notice-card">
      <div className="ncard__head">
        <span className="ncard__badge">
          <Icon name={grounded ? 'lock' : 'alert'} />
          {noticeLevelLabel[notice.level]}
        </span>
        <span className="ncard__state">{grounded ? de.notices.grounded : de.notices.airworthy}</span>
      </div>
      <h3 className="ncard__title">{notice.title}</h3>
      {notice.body && <p className="ncard__body">{notice.body}</p>}
      <p className="ncard__meta">
        {notice.createdBy ? `${notice.createdBy.name}, ` : ''}
        {formatDate(notice.createdAt)}
        {notice.validUntil ? ` · ${de.notices.validUntil(formatNoticeDay(notice.validUntil))}` : ''}
      </p>
      <div className="ncard__actions">
        {notice.acknowledgedAt ? (
          <span className="ncard__read">
            <Icon name="check" />
            {de.notices.acknowledged(formatDateTime(notice.acknowledgedAt))}
          </span>
        ) : (
          <button type="button" className="btn btn--sm" disabled={busy} onClick={acknowledge}>
            <Icon name="check" />
            {de.notices.acknowledge}
          </button>
        )}
        {editor && !asking && (
          <>
            <button type="button" className="btn btn--sm btn--ghost" onClick={onEdit}>
              <Icon name="edit" />
              {de.notices.edit}
            </button>
            <button type="button" className="btn btn--sm btn--ghost" onClick={() => setAsking(true)}>
              <Icon name="resolve" />
              {de.notices.resolve}
            </button>
          </>
        )}
        {editor && asking && (
          <span className="ncard__ask" role="group" aria-label={de.notices.resolveConfirm}>
            <span>{de.notices.resolveAsk}</span>
            <button type="button" className="btn btn--sm btn--danger" disabled={busy} onClick={resolve}>
              {de.notices.resolve}
            </button>
            <button type="button" className="btn btn--sm" onClick={() => setAsking(false)}>
              {tx.app.cancel}
            </button>
          </span>
        )}
      </div>
      {editor && notice.readers && (
        <details className="ncard__readers">
          <summary>{notice.readers.length ? de.notices.readBy(notice.readers.length) : de.notices.readByNobody}</summary>
          {notice.readers.length > 0 && (
            <ul>
              {notice.readers.map((r) => (
                <li key={r.person.id}>{de.notices.by(r.person.name, formatDateTime(r.acknowledgedAt))}</li>
              ))}
            </ul>
          )}
        </details>
      )}
    </article>
  );
}

function NoticeHistory({ aircraftId }: { aircraftId: string }) {
  const history = useNoticeHistory(aircraftId, true);
  const gone = (history.data ?? []).filter((n) => n.resolvedAt !== null || (n.validUntil !== null && n.validUntil < berlinDay(new Date())));
  return (
    <div className="notices__history" data-testid="notice-history">
      <h3 className="notices__subtitle">{de.notices.history}</h3>
      {history.isPending ? (
        <p className="muted">{tx.app.loading}</p>
      ) : gone.length === 0 ? (
        <p className="muted">{de.notices.historyEmpty}</p>
      ) : (
        <ul>
          {gone.map((n) => (
            <li key={n.id}>
              <strong>{noticeLevelLabel[n.level]}</strong> · {n.title}
              <span className="muted">
                {' '}
                ·{' '}
                {n.resolvedAt && n.resolvedBy
                  ? de.notices.resolvedBy(n.resolvedBy.name, formatDate(n.resolvedAt))
                  : n.validUntil
                    ? de.notices.validUntil(formatNoticeDay(n.validUntil))
                    : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function NoticeSheet({
  aircraftId,
  registration,
  notice,
  onClose,
}: {
  aircraftId: string;
  registration: string;
  notice: AircraftNotice | null;
  onClose: () => void;
}) {
  const id = useId();
  const write = useWrite();
  const settings = useSettings();
  const [level, setLevel] = useState<NoticeLevel>(notice?.level ?? 'info');
  const [title, setTitle] = useState(notice?.title ?? '');
  const [body, setBody] = useState(notice?.body ?? '');
  const [validUntil, setValidUntil] = useState(notice?.validUntil ?? '');
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const today = berlinDay(new Date());
  // A grounding that exists stays choosable after the club switched it off; a new one does not.
  const levels = noticeLevelsFor(settings.data?.aircraftGrounding).concat(
    notice?.level === 'gesperrt' && !settings.data?.aircraftGrounding ? ['gesperrt' as const] : [],
  );
  const trimmed = title.trim();
  const titleError =
    trimmed.length < NOTICE_TITLE_MIN || trimmed.length > NOTICE_TITLE_MAX ? de.fleet.textLength('Der Hinweis', NOTICE_TITLE_MIN, NOTICE_TITLE_MAX) : null;

  const save = async () => {
    setShowErrors(true);
    if (titleError) return;
    const fields = { level, title: trimmed, body: body.trim() || null, validUntil: validUntil || null };
    setSaving(true);
    const result = notice
      ? await write({ run: (s) => s.updateNotice(notice.id, fields, { expectedUpdatedAt: notice.updatedAt }), success: de.notices.saved })
      : await write({ run: (s) => s.createNotice({ aircraftId, ...fields }), success: de.notices.saved });
    setSaving(false);
    if (result) onClose();
  };

  return (
    <Sheet
      title={notice ? de.notices.edit : de.notices.add}
      kicker={<span className="mono label">{registration}</span>}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {tx.app.cancel}
          </button>
          <button type="button" className="btn btn--primary" disabled={saving} onClick={save}>
            <Icon name="check" />
            {tx.app.save}
          </button>
        </>
      }
    >
      <ChoiceChips
        label={de.notices.levelLabel}
        value={level}
        options={levels.map((l) => ({ value: l, label: noticeLevelLabel[l], className: `chip--notice-${l}` }))}
        onChange={setLevel}
      />
      <p className={`field__hint notice-hint notice-hint--${level}`}>{de.notices.levelHint[level]}</p>
      <div className="field">
        <label className="field__label" htmlFor={`${id}-t`}>
          {de.notices.titleLabel}
        </label>
        <input
          id={`${id}-t`}
          className="input"
          value={title}
          maxLength={NOTICE_TITLE_MAX}
          placeholder={de.notices.titlePlaceholder}
          onChange={(e) => setTitle(e.target.value)}
          aria-invalid={showErrors && titleError !== null}
        />
        {showErrors && titleError && <p className="field__error">{titleError}</p>}
      </div>
      <div className="field">
        <label className="field__label" htmlFor={`${id}-b`}>
          {de.notices.bodyLabel}
        </label>
        <textarea id={`${id}-b`} className="textarea" value={body} maxLength={NOTICE_BODY_MAX} onChange={(e) => setBody(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label" htmlFor={`${id}-v`}>
          {de.notices.validUntilLabel}
        </label>
        <input id={`${id}-v`} className="input" type="date" min={today} value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
        <p className="field__hint">{de.notices.validUntilHint}</p>
      </div>
    </Sheet>
  );
}

/** On top of the report sheet when the aircraft is grounded: reporting stays possible (owner, 17.10.2026). */
export function GroundedWarning({ aircraftId }: { aircraftId: string }) {
  const notices = useNotices();
  const grounding = noticesOfAircraft(notices.data, aircraftId).find((n) => n.level === 'gesperrt');
  if (!grounding) return null;
  return (
    <div className="notice notice--error grounded-warning" role="status" data-testid="grounded-warning">
      <Icon name="lock" />
      <div>
        <strong>{de.notices.reportWarning(grounding.title)}</strong>
        <p className="field__hint">{de.notices.reportWarningHint}</p>
      </div>
    </div>
  );
}
